import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Table } from "./common/Table";
import { Loading } from "./common/Loading";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Plus, ChevronLeft, MessageSquare } from "lucide-react";
import ConversationThread from "./Sms/ConversationThread";
import { listSmsConversations, sendSmsFromConversation } from "../services/extension.service";

const apiUrl =
  window._env_?.REACT_APP_API_URL ||
  "https://app.mylocaleventrentals.com/api";

const formatMoney = (v) => {
  const n = parseFloat(v);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
};

/** Same rule as paymentService.updateInvoiceStatus — amounts that count as received */
function paymentCountsAsReceived(status) {
  const s = String(status || "").toLowerCase();
  return s === "completed" || s === "paid";
}

const QUOTE_STATUS_CLASS = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
  cancelled: "bg-red-100 text-red-600",
  converted: "bg-purple-100 text-purple-800",
};

function CustomerTotalBusinessSummary({
  totalRealized,
  totalInvoicedAll,
  outstanding,
  invoiceCount,
  qualifyingPaymentCount,
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm h-full flex flex-col">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        At a glance
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
        <div className="sm:border-r sm:border-gray-200 sm:pr-4">
          <p className="text-xs text-gray-600">Received</p>
          <p className="text-xl md:text-2xl font-semibold text-gray-900 tabular-nums">
            {formatMoney(totalRealized)}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {qualifyingPaymentCount} qualifying payment
            {qualifyingPaymentCount === 1 ? "" : "s"}
          </p>
        </div>
        <div className="sm:border-r sm:border-gray-200 sm:pr-4">
          <p className="text-xs text-gray-600">Invoiced</p>
          <p className="text-lg font-semibold text-gray-900 tabular-nums">
            {formatMoney(totalInvoicedAll)}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {invoiceCount} invoice{invoiceCount === 1 ? "" : "s"}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Outstanding (est.)</p>
          <p className="text-lg font-semibold text-gray-900 tabular-nums">
            {formatMoney(outstanding)}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Unpaid balance on invoices</p>
        </div>
      </div>
      <details className="mt-3 text-xs text-gray-600 border-t border-gray-200 pt-2">
        <summary className="cursor-pointer select-none text-gray-700 hover:text-gray-900">
          How these numbers are calculated
        </summary>
        <p className="mt-2 leading-relaxed pl-1 border-l-2 border-gray-300">
          <strong>Received</strong> sums payments with status <strong>paid</strong> or{" "}
          <strong>completed</strong> on this customer&apos;s invoices.{" "}
          <strong>Outstanding</strong> is invoice totals minus those received payments
          (estimate when there are partial payments).
        </p>
      </details>
    </div>
  );
}

const CustomerView = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { logout, getToken } = useAuth();

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsConversation, setSmsConversation] = useState(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsSuccess, setSmsSuccess] = useState('');

  const orderColumns = [
    { key: "formattedOrderNumber", label: "Order Number", sortable: true },
    { key: "paymentStatus", label: "Payment Status", sortable: true },
    { key: "status", label: "Order Status" },
    { key: "startDate", label: "Start Date", sortable: true },
    { key: "endDate", label: "End Date", sortable: true },
    { key: "createdAt", label: "Created Date" },
  ];

  const quoteColumns = [
    { key: "quote_number", label: "Quote #", sortable: true },
    {
      key: "status",
      label: "Status",
      render: (value) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
            QUOTE_STATUS_CLASS[value] || "bg-gray-100 text-gray-700"
          }`}
        >
          {value || "draft"}
        </span>
      ),
    },
    {
      key: "total_amount",
      label: "Total",
      sortable: true,
      render: (v) => formatMoney(v),
    },
    { key: "due_date", label: "Valid Until", sortable: true },
    { key: "createdAt", label: "Created", sortable: true },
  ];

  const invoiceColumns = [
    { key: "invoiceNumber", label: "Invoice #", sortable: true },
    { key: "status", label: "Status", sortable: true },
    {
      key: "total_amount",
      label: "Total",
      sortable: true,
      render: (v) => formatMoney(v),
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      render: (_v, row) => row.issue_date || row.date || "—",
    },
    { key: "createdAt", label: "Created", sortable: true },
  ];

  const paymentColumns = [
    { key: "payment_number", label: "Payment #", sortable: true },
    { key: "date", label: "Payment Date", sortable: true },
    { key: "method", label: "Method", sortable: true },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (v) => formatMoney(v),
    },
    { key: "status", label: "Status", sortable: true },
    {
      key: "invoice_ref",
      label: "Invoice #",
      sortable: false,
      render: (_v, row) => row.invoice?.invoiceNumber ?? "—",
    },
  ];

  const getAuthHeaders = () => {
    const token = getToken ? getToken() : localStorage.getItem("token");
    if (!token) {
      logout();
      throw new Error("No auth token");
    }
    return { "x-auth-token": token };
  };

  const fetchCustomerData = async () => {
    const res = await axios.get(`${apiUrl}/customers/${uuid}`, {
      headers: getAuthHeaders(),
    });
    setCustomer(res.data.data);
  };

  const fetchCustomerOrders = async () => {
    const res = await axios.get(
      `${apiUrl}/order/customerorders/${uuid}`,
      { headers: getAuthHeaders() }
    );
    setOrders(res.data || []);
  };

  const fetchCustomerQuotes = async () => {
    const res = await axios.get(
      `${apiUrl}/invoice/quotes?customerId=${encodeURIComponent(uuid)}`,
      { headers: getAuthHeaders() }
    );
    setQuotes(res.data?.data || []);
  };

  const fetchCustomerInvoices = async () => {
    const res = await axios.get(
      `${apiUrl}/invoice?customerId=${encodeURIComponent(
        uuid
      )}&document_type=invoice`,
      { headers: getAuthHeaders() }
    );
    setInvoices(Array.isArray(res.data) ? res.data : res.data?.data || []);
  };

  const fetchCustomerPayments = async () => {
    const res = await axios.get(
      `${apiUrl}/payment/customer/${encodeURIComponent(uuid)}/payments`,
      { headers: getAuthHeaders() }
    );
    setPayments(res.data?.data || []);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchCustomerData(),
          fetchCustomerOrders(),
          fetchCustomerQuotes(),
          fetchCustomerInvoices(),
          fetchCustomerPayments(),
        ]);
      } catch (err) {
        console.error(err);
        navigate("/CustomerList");
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line
  }, [uuid]);

  const handleNewOrder = () => {
    navigate(`/orders/customer/${uuid}/new`);
  };

  const handleNewQuote = () => {
    navigate(`/quotes/new?customerId=${uuid}`);
  };

  const handleNewInvoice = () => {
    navigate(`/invoice/new?customerId=${uuid}`);
  };

  const handleNewPayment = () => {
    navigate("/payment/new");
  };

  const handleEditCustomer = () => {
    navigate(`/customer/${uuid}`);
  };

  const paidByInvoice = useMemo(() => {
    const m = {};
    for (const p of payments) {
      if (!paymentCountsAsReceived(p.status)) continue;
      const id = p.invoice_id;
      if (!id) continue;
      m[id] = (m[id] || 0) + (parseFloat(p.amount) || 0);
    }
    return m;
  }, [payments]);

  const totalRealized = useMemo(
    () =>
      payments
        .filter((p) => paymentCountsAsReceived(p.status))
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
    [payments]
  );

  const totalInvoicedAll = useMemo(
    () =>
      invoices.reduce((sum, inv) => {
        const n = parseFloat(inv.total_amount);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0),
    [invoices]
  );

  const outstanding = useMemo(
    () =>
      invoices.reduce((sum, inv) => {
        const t = parseFloat(inv.total_amount) || 0;
        const paid = paidByInvoice[inv.uuid] || 0;
        return sum + Math.max(0, t - paid);
      }, 0),
    [invoices, paidByInvoice]
  );

  const qualifyingPaymentCount = useMemo(
    () => payments.filter((p) => paymentCountsAsReceived(p.status)).length,
    [payments]
  );

  if (loading) return <Loading />;

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-md rounded-md scroll-smooth">
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/CustomerList")}
            className="inline-flex items-center gap-0.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            Back to customers
          </button>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {customer.firstname} {customer.lastname}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Profile, billing summary, and activity in one place.
          </p>
        </div>
        <div
          className="flex flex-wrap gap-2 lg:justify-end"
          aria-label="Quick create actions"
        >
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
            onClick={handleNewOrder}
          >
            New order
          </button>
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
            onClick={handleNewQuote}
          >
            New quote
          </button>
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
            onClick={handleNewInvoice}
          >
            New invoice
          </button>
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
            onClick={handleNewPayment}
          >
            Record payment
          </button>
        </div>
      </div>

      <nav
        aria-label="On this page"
        className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
      >
        <a href="#section-contact" className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900">
          Contact
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a href="#section-orders" className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900">
          Orders ({orders.length})
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a href="#section-quotes" className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900">
          Quotes ({quotes.length})
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a href="#section-invoices" className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900">
          Invoices ({invoices.length})
        </a>
        <span className="text-gray-300 select-none" aria-hidden>
          ·
        </span>
        <a href="#section-payments" className="rounded px-1.5 py-0.5 hover:bg-white hover:text-gray-900">
          Payments ({payments.length})
        </a>
      </nav>

      <div
        id="section-contact"
        className="grid gap-4 lg:grid-cols-2 mb-10 scroll-mt-24"
      >
        <Card sx={{ width: "100%", height: "100%" }}>
          <CardContent>
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3 mb-3">
              <Typography variant="h6" component="h2">
                Contact
              </Typography>
              <Button variant="outlined" size="small" onClick={handleEditCustomer}>
                Edit
              </Button>
            </div>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography className="mb-3">
              {customer.firstname} {customer.lastname}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary">
              Email
            </Typography>
            {customer.email ? (
              <Typography
                component="a"
                href={`mailto:${encodeURIComponent(customer.email)}`}
                className="mb-3 block text-blue-700 hover:underline break-all"
              >
                {customer.email}
              </Typography>
            ) : (
              <Typography className="mb-3 text-gray-500">—</Typography>
            )}

            <Typography variant="subtitle2" color="text.secondary">
              Phone
            </Typography>
            {customer.phone ? (
              <Typography
                component="a"
                href={`tel:${String(customer.phone).replace(/[^\d+]/g, "")}`}
                className="block text-blue-700 hover:underline"
              >
                {customer.phone}
              </Typography>
            ) : (
              <Typography className="text-gray-500">—</Typography>
            )}
          </CardContent>
        </Card>

        <CustomerTotalBusinessSummary
          totalRealized={totalRealized}
          totalInvoicedAll={totalInvoicedAll}
          outstanding={outstanding}
          invoiceCount={invoices.length}
          qualifyingPaymentCount={qualifyingPaymentCount}
        />
      </div>

      <div id="section-orders" className="mt-2 scroll-mt-24">
        <h2 className="text-xl font-semibold text-gray-900">
          Orders
          <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-gray-500 rounded-full">
            {orders.length}
          </span>
        </h2>

        <div className="flex justify-left mb-4 mt-4">
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={handleNewOrder}
          >
            <Plus size={20} />
            New Order
          </button>
        </div>

        <Table
          data={orders}
          columns={orderColumns}
          paginated
          pageSize={10}
          rowKey="uuid"
          onRowClick={(order) => navigate(`/orders/${order.uuid}`)}
        />
      </div>

      <div id="section-quotes" className="mt-10 scroll-mt-24">
        <h2 className="text-xl font-semibold text-gray-900">
          Quotes
          <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-gray-500 rounded-full">
            {quotes.length}
          </span>
        </h2>
        <div className="flex justify-left mb-4 mt-4">
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={handleNewQuote}
          >
            <Plus size={20} />
            New Quote
          </button>
        </div>
        <Table
          data={quotes}
          columns={quoteColumns}
          paginated
          pageSize={10}
          rowKey="uuid"
          onRowClick={(row) => navigate(`/quotes/${row.uuid}`)}
        />
      </div>

      <div id="section-invoices" className="mt-10 scroll-mt-24">
        <h2 className="text-xl font-semibold text-gray-900">
          Invoices
          <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-gray-500 rounded-full">
            {invoices.length}
          </span>
        </h2>
        <div className="flex justify-left mb-4 mt-4">
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={handleNewInvoice}
          >
            <Plus size={20} />
            New Invoice
          </button>
        </div>
        <Table
          data={invoices}
          columns={invoiceColumns}
          paginated
          pageSize={10}
          rowKey="uuid"
          onRowClick={(row) => navigate(`/invoice/${row.uuid}/edit`)}
        />
      </div>

      <div id="section-payments" className="mt-10 scroll-mt-24">
        <h2 className="text-xl font-semibold text-gray-900">
          Payments
          <span className="ml-2 px-2 py-1 text-xs font-semibold text-white bg-gray-500 rounded-full">
            {payments.length}
          </span>
        </h2>
        <div className="flex justify-left mb-4 mt-4">
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={handleNewPayment}
          >
            <Plus size={20} />
            New Payment
          </button>
        </div>
        <Table
          data={payments}
          columns={paymentColumns}
          paginated
          pageSize={10}
          rowKey="uuid"
          onRowClick={(row) => navigate(`/payment/${row.uuid}`)}
        />
      </div>

      {/* ── SMS Section ─────────────────────────────────────── */}
      <div id="section-sms" className="mt-10 scroll-mt-24">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <MessageSquare size={20} />
            SMS
            {smsConversation && (
              <span className="text-xs font-normal text-gray-500">
                — {smsConversation.phone_number}
              </span>
            )}
          </h2>
          {!showSms && customer?.phone && (
            <button
              type="button"
              className="btn-primary flex items-center gap-2 text-sm"
              onClick={async () => {
                setSmsLoading(true);
                try {
                  const res = await listSmsConversations({ customer_id: uuid });
                  const convs = res.data?.data || [];
                  if (convs.length > 0) {
                    setSmsConversation(convs[0]);
                  } else {
                    // No conversation yet — will be created on first send
                    setSmsConversation({ id: null, phone_number: customer.phone });
                  }
                  setShowSms(true);
                } catch {
                  setSmsError('Could not load SMS conversation');
                } finally {
                  setSmsLoading(false);
                }
              }}
            >
              <MessageSquare size={16} />
              {smsLoading ? 'Loading…' : 'View Conversation'}
            </button>
          )}
          {!customer?.phone && !showSms && (
            <span className="text-sm text-gray-400">No phone on file — add a phone number to enable SMS</span>
          )}
        </div>

        {smsError && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{smsError}</div>
        )}

        {showSms && smsConversation && (
          <div className="rounded-lg border border-gray-200 overflow-hidden" style={{ height: '500px' }}>
            <ConversationThread
              conversationId={smsConversation.id}
              phoneNumber={smsConversation.phone_number || customer.phone}
              onClose={() => { setShowSms(false); setSmsConversation(null); }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerView;
